/**
 * RapportMensuelPage.jsx
 *
 * Rapport mensuel : comparaison mois par mois contrat vs consommé,
 * valorisation par profil, chart évolution, table détaillée.
 * Self-contained, utilise useContratPageData via ContratPageShared.
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  MaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';

import {
  ConsumptionDeltaCell,
  ContractAlerts,
  ContractFiltersBar,
  fmtDays,
  fmtHours,
  formatCountDelta,
  formatCurrency,
  formatDaysDelta,
  formatHoursDelta,
  RentaValueCell,
  useContratPageData,
  ValueConsumptionCell,
} from './ContratPageShared';

// ─── Palette sémantique ────────────────────────────────────────────────────────

const COLORS = {
  consumed: '#378ADD',
  remaining: '#1D9E75',
  overrun: '#E24B4A',
};

// ─── Tooltip custom ────────────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload, label, profile }) => {
  if (!active || !payload?.length) return null;

  // Récupère les valeurs depuis le payload
  const get = (name) => payload.find((p) => p.name === name)?.value ?? 0;
  const consumed = get('Consommé');
  const remaining = get('Rentabilité restante');
  const overrun = get('Dépassement');
  // Breakdown par profil (stocké en extras dans le premier point)
  const extras = payload[0]?.payload ?? {};

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: '0.5px solid',
        borderColor: 'divider',
        minWidth: 200,
        fontSize: 12,
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        {label}
      </Typography>

      <Stack spacing={0.4}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" color="text.secondary">Contrat</Typography>
          <Typography variant="caption">{formatCurrency(extras._contract ?? 0)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" sx={{ color: COLORS.consumed }}>Consommé</Typography>
          <Typography variant="caption">{formatCurrency(consumed)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" sx={{ color: COLORS.remaining }}>Restant rentable</Typography>
          <Typography variant="caption">{formatCurrency(remaining)}</Typography>
        </Stack>
        {overrun > 0 && (
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="caption" sx={{ color: COLORS.overrun }}>Dépassement</Typography>
            <Typography variant="caption">{formatCurrency(overrun)}</Typography>
          </Stack>
        )}

        {/* Breakdown par profil en vue "all" */}
        {profile === 'all' && (
          <>
            <Box sx={{ borderTop: '0.5px solid', borderColor: 'divider', mt: 0.5, pt: 0.5 }}>
              {[
                { label: 'Admin', key: '_adminConsumed' },
                { label: 'Ingénieur', key: '_ingConsumed' },
                { label: 'Pilotage', key: '_pilConsumed' },
              ].map(({ label: l, key }) => (
                <Stack key={key} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="caption" color="text.secondary">{l}</Typography>
                  <Typography variant="caption">{formatCurrency(extras[key] ?? 0)}</Typography>
                </Stack>
              ))}
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};

// ─── Légende custom ────────────────────────────────────────────────────────────

const LegendItems = () => (
  <Stack direction="row" spacing={2.5} flexWrap="wrap" mt={1}>
    {[
      { color: COLORS.consumed, label: 'Consommé (dans contrat)' },
      { color: COLORS.remaining, label: 'Rentabilité restante' },
      { color: COLORS.overrun, label: 'Dépassement' },
    ].map(({ color, label }) => (
      <Stack key={label} direction="row" alignItems="center" spacing={0.7}>
        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color, flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Stack>
    ))}
  </Stack>
);

// ─── KPI cards ─────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, color }) => (
  <Box
    sx={{
      bgcolor: 'background.default',
      borderRadius: 1,
      p: '10px 14px',
      border: '0.5px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={500} sx={{ color: color ?? 'text.primary' }}>
      {value}
    </Typography>
  </Box>
);

// ─── Page principale ───────────────────────────────────────────────────────────

export default function RapportMensuelPage() {
  const [selectedValueProfile, setSelectedValueProfile] = useState('all');
  const [selectedValueMonthKey, setSelectedValueMonthKey] = useState(null); // null = tous

  const {
    availableYears,
    applyFilters,
    canApplyFilters,
    clientAutocompleteOptions,
    competenceCatalog,
    customEndDate,
    customStartDate,
    dateMode,
    error,
    filteredContracts,
    mismatchContracts,
    monthlyComparisonRows,
    monthlyValueChartData,
    rootOrganizationOptions,
    hasAppliedFilters,
    hasPendingFilterChanges,
    selectedInfogeranceFilter,
    selectedOrganizationId,
    selectedRootOrganizationId,
    selectedYear,
    setCustomEndDate,
    setCustomStartDate,
    setDateMode,
    setSelectedInfogeranceFilter,
    setSelectedOrganizationId,
    setSelectedRootOrganizationId,
    setSelectedYear,
    loading,
    supportEntriesLoading,
  } = useContratPageData({
    autoLoad: false,
    initialSelectedYear: '',
    requireOrganizationSelection: true,
  });

  const isReadyToDisplay = hasAppliedFilters && !hasPendingFilterChanges;

  // ── Chart data ───────────────────────────────────────────────────────────────

  const chartData = useMemo(() => {
    const source = selectedValueMonthKey
      ? monthlyValueChartData.filter((item) => item.monthKey === selectedValueMonthKey)
      : monthlyValueChartData;

    return source.map((item) => {
      const profileKey =
        selectedValueProfile === 'admin'
          ? 'admin'
          : selectedValueProfile === 'ingenieur'
          ? 'ingenieur'
          : selectedValueProfile === 'pilotage'
          ? 'pilotage'
          : null;

      if (profileKey) {
        const cK = `${profileKey}ConsumedValue`;
        const kK = `${profileKey}ContractValue`;
        const consumedValue = Number(item[cK] || 0);
        const contractValue = Number(item[kK] || 0);
        return {
          label: item.label,
          monthKey: item.monthKey,
          'Consommé': Math.min(consumedValue, contractValue),
          'Rentabilité restante': Math.max(contractValue - consumedValue, 0),
          'Dépassement': Math.max(consumedValue - contractValue, 0),
          // Extras pour le tooltip
          _contract: contractValue,
        };
      }

      // Vue tous les profils : on agrège
      const adminC = Number(item.adminConsumedValue || 0);
      const adminK = Number(item.adminContractValue || 0);
      const ingC   = Number(item.ingenieurConsumedValue || 0);
      const ingK   = Number(item.ingenieurContractValue || 0);
      const pilC   = Number(item.pilotageConsumedValue || 0);
      const pilK   = Number(item.pilotageContractValue || 0);

      const totalConsumed   = Math.min(adminC, adminK) + Math.min(ingC, ingK) + Math.min(pilC, pilK);
      const totalRemaining  = Math.max(adminK - adminC, 0) + Math.max(ingK - ingC, 0) + Math.max(pilK - pilC, 0);
      const totalOverrun    = Math.max(adminC - adminK, 0) + Math.max(ingC - ingK, 0) + Math.max(pilC - pilK, 0);
      const totalContract   = adminK + ingK + pilK;

      return {
        label: item.label,
        monthKey: item.monthKey,
        'Consommé': totalConsumed,
        'Rentabilité restante': totalRemaining,
        'Dépassement': totalOverrun,
        // Extras pour le tooltip
        _contract: totalContract,
        _adminConsumed: Math.min(adminC, adminK),
        _ingConsumed:   Math.min(ingC, ingK),
        _pilConsumed:   Math.min(pilC, pilK),
      };
    });
  }, [monthlyValueChartData, selectedValueMonthKey, selectedValueProfile]);

  // ── KPIs agrégés ─────────────────────────────────────────────────────────────

  const kpis = useMemo(() => {
    const totalContract  = chartData.reduce((a, d) => a + (d._contract ?? 0), 0);
    const totalConsumed  = chartData.reduce((a, d) => a + (d['Consommé'] ?? 0), 0);
    const totalRemaining = chartData.reduce((a, d) => a + (d['Rentabilité restante'] ?? 0), 0);
    const totalOverrun   = chartData.reduce((a, d) => a + (d['Dépassement'] ?? 0), 0);
    const overrunMonths  = chartData.filter((d) => (d['Dépassement'] ?? 0) > 0).length;
    return { totalContract, totalConsumed, totalRemaining, totalOverrun, overrunMonths };
  }, [chartData]);

  // ── Table ─────────────────────────────────────────────────────────────────────

  const table = useMaterialReactTable({
    columns: [
      { accessorKey: 'contractName', header: 'Contrat' },
      { accessorKey: 'client', header: 'Client' },
      { accessorKey: 'label', header: 'Mois' },
      {
        accessorKey: 'isOverrun',
        header: 'Statut',
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.isOverrun ? 'Dépassement' : 'OK'}
            color={row.original.isOverrun ? 'warning' : 'success'}
          />
        ),
      },
      {
        id: 'adm',
        header: 'Admin',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedAdmin}
            delta={row.original.deltaAdmin}
            contractValue={row.original.contractAdmin}
            consumedFormatter={fmtHours}
            deltaFormatter={formatHoursDelta}
          />
        ),
      },
      {
        id: 'ing',
        header: 'Ingénieur',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedIngenieur}
            delta={row.original.deltaIngenieur}
            contractValue={row.original.contractIngenieur}
            consumedFormatter={fmtHours}
            deltaFormatter={formatHoursDelta}
          />
        ),
      },
      {
        id: 'pil',
        header: 'Pilotage',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedPilotage}
            delta={row.original.deltaPilotage}
            contractValue={row.original.contractPilotage}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'dAdm',
        header: 'Jour admin',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedCadreAdminDays}
            delta={row.original.deltaCadreAdminDays}
            contractValue={row.original.contractCadreAdminDays}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'dIng',
        header: 'Jour ingé',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedCadreIngenieurDays}
            delta={row.original.deltaCadreIngenieurDays}
            contractValue={row.original.contractCadreIngenieurDays}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'dep',
        header: 'Déplacements',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedTravel}
            delta={row.original.deltaTravel}
            contractValue={row.original.contractTravel}
            consumedFormatter={(v) => `${Number(v || 0)}`}
            deltaFormatter={formatCountDelta}
          />
        ),
      },
      {
        id: 'val',
        header: 'Valeur conso.',
        Cell: ({ row }) => (
          <ValueConsumptionCell
            consumedValue={row.original.consumedValue}
            contractValue={row.original.contractValue}
          />
        ),
      },
      {
        id: 'renta',
        header: 'Rentabilité',
        Cell: ({ row }) => (
          <RentaValueCell
            rentaValue={row.original.rentaValue}
            contractValue={row.original.contractValue}
          />
        ),
      },
      {
        accessorKey: 'rentaRate',
        header: 'Renta %',
        Cell: ({ row }) => `${Number(row.original.rentaRate || 0).toFixed(1)} %`,
      },
    ],
    data: monthlyComparisonRows,
    state: { isLoading: loading || supportEntriesLoading },
    enablePagination: false,
    initialState: { density: 'compact' },
    renderToolbarInternalActions: ({ table: t }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={t} />
        <MRT_ToggleFiltersButton table={t} />
        <MRT_ToggleGlobalFilterButton table={t} />
        <MRT_ToggleFullScreenButton table={t} />
      </Box>
    ),
  });

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── En-tête ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Rapport mensuel</Typography>
          <Typography variant="caption" color="text.secondary">
            Comparaison mois par mois — heures contractuelles vs consommées, valorisation.
          </Typography>
        </Box>
        {supportEntriesLoading && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">Chargement des imputations…</Typography>
          </Stack>
        )}
      </Stack>

      {/* ── Filtres ── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <ContractFiltersBar
          applyDisabled={!canApplyFilters || loading || supportEntriesLoading}
          availableYears={availableYears}
          clientAutocompleteOptions={clientAutocompleteOptions}
          customEndDate={customEndDate}
          customStartDate={customStartDate}
          dateMode={dateMode}
          onApply={applyFilters}
          onChangeCustomEndDate={setCustomEndDate}
          onChangeCustomStartDate={setCustomStartDate}
          onChangeDateMode={setDateMode}
          onChangeInfogerance={setSelectedInfogeranceFilter}
          onChangeOrganization={setSelectedOrganizationId}
          onChangeRootOrganization={setSelectedRootOrganizationId}
          onChangeYear={setSelectedYear}
          rootOrganizationOptions={rootOrganizationOptions}
          selectedInfogeranceFilter={selectedInfogeranceFilter}
          selectedOrganizationId={selectedOrganizationId}
          selectedRootOrganizationId={selectedRootOrganizationId}
          selectedYear={selectedYear}
          showApplyButton
        />
      </Paper>

      {!hasAppliedFilters && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Sélectionne un client et une année, puis clique sur Valider pour charger le rapport.
        </Alert>
      )}

      {hasPendingFilterChanges && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Les filtres ont changé. Clique sur Valider pour recharger le rapport avec la nouvelle sélection.
        </Alert>
      )}

      {/* ── Alertes & erreurs ── */}
      {isReadyToDisplay && (
        <>
          <ContractAlerts
            competenceCatalog={competenceCatalog}
            mismatchContracts={mismatchContracts}
            supportEntriesLoading={supportEntriesLoading}
          />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        </>
      )}

      {/* ── Chart valeur mensuelle ── */}
      {isReadyToDisplay && (
        <Paper sx={{ p: 2, mb: 2 }}>
          {/* Titre + sélecteur de profil en pills */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={2}
            mb={2}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Valeur mensuelle — consommé et rentabilité restante
            </Typography>

            {/* Pills profil */}
            <Stack direction="row" spacing={0.75} flexWrap="wrap">
              {[
                { value: 'all',       label: 'Tous' },
                { value: 'admin',     label: 'Admin' },
                { value: 'ingenieur', label: 'Ingénieur' },
                { value: 'pilotage',  label: 'Pilotage' },
              ].map(({ value, label }) => (
                <Chip
                  key={value}
                  label={label}
                  size="small"
                  onClick={() => setSelectedValueProfile(value)}
                  variant={selectedValueProfile === value ? 'filled' : 'outlined'}
                  color={selectedValueProfile === value ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer', fontWeight: selectedValueProfile === value ? 600 : 400 }}
                />
              ))}
            </Stack>
          </Stack>

          {/* Chips de mois */}
          <Stack direction="row" spacing={0.75} flexWrap="wrap" mb={2}>
            <Chip
              label="Tous les mois"
              size="small"
              onClick={() => setSelectedValueMonthKey(null)}
              variant={selectedValueMonthKey === null ? 'filled' : 'outlined'}
              color={selectedValueMonthKey === null ? 'default' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
            {monthlyValueChartData.map((item) => (
              <Chip
                key={item.monthKey}
                label={item.label}
                size="small"
                onClick={() =>
                  setSelectedValueMonthKey(
                    selectedValueMonthKey === item.monthKey ? null : item.monthKey,
                  )
                }
                variant={selectedValueMonthKey === item.monthKey ? 'filled' : 'outlined'}
                color={selectedValueMonthKey === item.monthKey ? 'primary' : 'default'}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Stack>

          {/* KPI cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 1.25,
              mb: 2,
            }}
          >
            <KpiCard label="Valeur contractuelle" value={formatCurrency(kpis.totalContract)} color="primary.main" />
            <KpiCard label="Valeur consommée" value={formatCurrency(kpis.totalConsumed)} />
            <KpiCard label="Rentabilité restante" value={formatCurrency(kpis.totalRemaining)} color="success.main" />
            <KpiCard
              label="Dépassements"
              value={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{formatCurrency(kpis.totalOverrun)}</span>
                  {kpis.overrunMonths > 0 && (
                    <Chip
                      label={`${kpis.overrunMonths} mois`}
                      size="small"
                      color="error"
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  )}
                </Stack>
              }
              color={kpis.totalOverrun > 0 ? 'error.main' : 'success.main'}
            />
          </Box>

          {/* Chart */}
          <Box sx={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,.06)" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) =>
                    v >= 1_000_000
                      ? `${(v / 1_000_000).toFixed(1)}M`
                      : v >= 1_000
                      ? `${(v / 1_000).toFixed(0)}k`
                      : v
                  }
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={52}
                />
                <RechartsTooltip
                  content={<CustomTooltip profile={selectedValueProfile} />}
                  cursor={{ fill: 'rgba(0,0,0,.04)' }}
                />

                {/* 3 séries unifiées — lisibles quelle que soit la vue */}
                <Bar dataKey="Consommé"            stackId="value" fill={COLORS.consumed}  radius={[0, 0, 0, 0]} maxBarSize={48} />
                <Bar dataKey="Rentabilité restante" stackId="value" fill={COLORS.remaining} radius={[0, 0, 0, 0]} maxBarSize={48} />
                <Bar dataKey="Dépassement"          stackId="value" fill={COLORS.overrun}   radius={[4, 4, 0, 0]} maxBarSize={48} />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>

          {/* Légende */}
          <LegendItems />
        </Paper>
      )}

      {/* ── Table ── */}
      {isReadyToDisplay && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            Détail mensuel par contrat — {filteredContracts.length} contrat(s)
          </Typography>
          <MaterialReactTable table={table} />
        </Paper>
      )}
    </Box>
  );
}